import { useCallback } from "react";
import { useQuery, useMutation } from "./urqlHooks";
import { useSelectedCompanyId } from "./tenantCompanyId";

const GETAGENDAS = `query GET_AGENDAS(
    $input: AgendaSearchInput!
    $pagination: PaginationInput!
    $search: String
  ) {
    paginatedSearchAgenda(
      input: $input
      pagination: $pagination
      search: $search
    ) {
      totalDocs
      docs {
        id
        name
        rut
        email
        secondaryEmails
        collectionEmails
        collectionEmailContacts {
          email
          contactName
        }
        address
        zone
        activity
        isClient
        isProvider
        bankRut
        bankId
        bankAccount
        bankAccountType
        paymentDays
        defaultCashflowArea
        paymentDaysClient
        paymentDaysProvider
        defaultCashflowAreaClient
        defaultCashflowAreaProvider
        defaultEERRAreaClient
        defaultEERRAreaProvider
        updatedAt
        worldWide
        nominasLabel
        collectionLabel
        bankAccounts {
          __typename @skip(if: true)
          bankAccountType
          bankAccount
          bankId
          bankRut
          email
          isMain
          id
        }
        labels
      }
    }
  }`;

export const GetAgendas = ({ input, pagination, search, pause: externalPause }) => {
  // Pause query only if no companyId or if explicitly paused externally
  const shouldPause = externalPause !== undefined 
    ? externalPause 
    : !input?.companyId;
  
  const [result, reexecuteQuery] = useQuery({
    query: GETAGENDAS,
    variables: { input, pagination, search: search || '' },
    pause: shouldPause,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const ALL_AGENDA = `query ALL_AGENDA(
  $companyId: String!
) {
  allAgendas(companyId: $companyId) {
      id
      name
      rut
      email
      isClient
      isProvider
      paymentDaysClient
      updatedAt
      collectionBusinessGroup
      collectionBusinessGroupId
      contactInfo {
        firstname
        lastname
      }
  }
}`;

export const GetAllAgendas = ({ companyId }) => {
  const [result, reexecuteQuery] = useQuery({
    query: ALL_AGENDA,
    variables: { companyId },
    pause: !companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const DELETEAGENDA = `mutation DELETE_AGENDA($id: String!, $companyId: String!) {
  deleteAgenda(id: $id, companyId: $companyId) {
    id
  }
}`;

export const Delete_Agenda = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [result, executeMutation] = useMutation(DELETEAGENDA);
  const wrappedExecute = useCallback(
    (vars) => executeMutation({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [executeMutation, selectedCompanyId],
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation: wrappedExecute };
};

const CREATEAGENDA = `mutation CREATE_AGENDA($input: AgendaInput!) {
  createAgenda(input: $input) {
    status
    message
    agenda {
      id
    }
  }
}`;

export const CreateAgenda = () => {
  const [result, executeMutation] = useMutation(CREATEAGENDA);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const UPDATEAGENDA = `mutation UPDATE_AGENDA($id: String!, $companyId: String!, $input: UpdateAgendaInput!) {
  updateAgenda(id: $id, companyId: $companyId, input: $input) {
    id
  }
}`;

export const UpdateAgenda = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [result, executeMutation] = useMutation(UPDATEAGENDA);
  const wrappedExecute = useCallback(
    (vars) => executeMutation({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [executeMutation, selectedCompanyId],
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation: wrappedExecute };
};

const UPDATE_COLLECTION_LABEL = `mutation UPDATE_COLLECTION_LABEL($agendaIds: [String!]!, $label: String!, $companyId: String!) {
  updateCollectionLabel(agendaIds: $agendaIds, label: $label, companyId: $companyId) {
    status
    message
    data
  }
}`;

export const UpdateCollectionLabel = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [result, executeMutation] = useMutation(UPDATE_COLLECTION_LABEL);
  const wrappedExecute = useCallback(
    (vars) => executeMutation({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [executeMutation, selectedCompanyId],
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation: wrappedExecute };
};

const UPDATE_AGENDA_FIELDS = `mutation UPDATE_AGENDA_FIELDS($id: String!, $companyId: String!, $input: ContactInfoInput!, $email: String!) {
  updateAgendaFields(id: $id, companyId: $companyId, input: $input, email: $email) {
    id
  }
}`;

export const UpdateContactAgenda = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [result, executeMutation] = useMutation(UPDATE_AGENDA_FIELDS);
  const wrappedExecute = useCallback(
    (vars) => executeMutation({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [executeMutation, selectedCompanyId],
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation: wrappedExecute };
};

const BULK_AGENDA = `mutation CREATE_BULK_AGENDAS($input: BulkAgendaInput!) {
  createBulkAgendas(input: $input) {
    Upserted {
      id
    }
    duplicados {
      id
      rut
      isClient
      isProvider
      name
      email
    }
  }
}
`;

export const BulkAgenda = () => {
  const [result, executeMutation] = useMutation(BULK_AGENDA);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DOWNLOAD_AGENDA = `mutation DOWNLOAD_AGENDA($companyId: String!, $isTemplate: Boolean) {
    generateAgendaExcel(companyId: $companyId, isTemplate: $isTemplate)
  }
`;

export const DownloadAgenda = () => {
  const [result, executeMutation] = useMutation(DOWNLOAD_AGENDA);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const AGENDA_BY_RUT = `query AgendaByRut(
  $rut: String!,
  $companyId: String!,
  $worldWide: String!
) {
  AgendaByRut(
    rut: $rut,
    companyId: $companyId,
    worldWide: $worldWide
  ) {
      id
      name
      rut
      email
      secondaryEmails
      collectionEmails
      collectionEmailContacts {
        email
        contactName
      }
      address
      zone
      activity
      isClient
      isProvider
      bankRut
      bankId
      bankAccount
      bankAccountType
      paymentDays
      defaultCashflowArea
      paymentDaysClient
      paymentDaysProvider
      defaultCashflowAreaClient
      defaultCashflowAreaProvider
      defaultEERRAreaClient
      defaultEERRAreaProvider
      updatedAt
      worldWide
      nominasLabel
      collectionLabel
      labels
      collectionAssigneeUserId
      collectionAssigneeName
      collectionAssigneeEmail
      collectionDecision
      collectionChannel
      notificationHistory {
        date
        userEmail
        userId
        recipientEmail
        ccEmails
        emailRecipients {
          email
          role
          name
          userId
          sourceType
          teamKey
          teamName
          emailStatus
          emailStatusAt
        }
        subject
        bodyHtml
        emailType
        totalAmount
        invoiceCount
        sesMessageId
        emailStatus
        emailStatusAt
        invoices {
          documentId
          documentLabel
          dueDate
          amount
        }
      }
      collectionClientStatus
      collectionBusinessLine
      collectionBusinessGroup
      collectionBusinessGroupId
      collectionComments {
        id
        comment
        date
        isPinned
        user {
          id
          name
          email
        }
      }
      collectionDocumentOverrides {
        documentId
        collectionDecision
        collectionChannel
        collectionResponse
        collectionNextSteps
      }
      collectionManagementHistory {
        date
        userId
        userName
        userEmail
        eventType
        previousValue
        newValue
        metadata
      }
      contactInfo {
        firstname
        lastname
        phone
      }
      bankAccounts {
        id
        email
        bankAccount
        bankAccountType
        isMain
      }
  }
}`;

export const AGENDA_BY_RUT_QUERY = AGENDA_BY_RUT;

export const AgendaByRut = (rut, companyId, worldWide) => {
  const [result, reexecuteQuery] = useQuery({
    query: AGENDA_BY_RUT,
    variables: { rut: rut || '', companyId, worldWide: worldWide || '' },
    pause: !rut || !companyId || rut.trim() === '', // No ejecutar si no hay RUT o companyId válidos
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

export const AGENDA_BY_NAME = `
  query AgendaByName(
    $name: String!,
    $companyId: String!
  ) {
    AgendaByName(
      name: $name,
      companyId: $companyId
    ) {
        id
        name
        rut
        defaultCashflowAreaProvider
        isClient
        isProvider
    }
  }
`;

export const AgendaByName = (name, companyId, pause = false) => {
  const [result, reexecuteQuery] = useQuery({
    query: AGENDA_BY_NAME,
    variables: { name, companyId},
    pause: pause || !name || !companyId, // Pause query if pause is true or if name/companyId are missing
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

export const FIND_AGENDA = `query FIND_AGENDA(
  $id: String!
  $companyId: String!
) {
  Agenda(
    id: $id
    companyId: $companyId
  ) {
      id
      name
      rut
      email
      secondaryEmails
      collectionEmails
      collectionEmailContacts {
        email
        contactName
      }
      address
      zone
      activity
      isClient
      isProvider
      bankRut
      bankId
      bankAccount
      bankAccountType
      paymentDays
      defaultCashflowArea
      paymentDaysClient
      paymentDaysProvider
      defaultCashflowAreaClient
      defaultCashflowAreaProvider
      defaultEERRAreaClient
      defaultEERRAreaProvider
      defaultEOAFAreaClient
      defaultEOAFAreaProvider
      defaultCostCenterClient
      defaultCostCenterProvider
      updatedAt
      worldWide
      nominasLabel
      collectionLabel
      collectionClientStatus
      collectionBusinessLine
      collectionBusinessGroup
      collectionBusinessGroupId
      outsideProviderId
      bankAccounts {
        __typename @skip(if: true)
        bankAccountType
        bankAccount
        bankId
        bankRut
        isMain
        email
        id
      }
      labels
  }
}`;

export const FindAgenda = (id) => {
  const selectedCompanyId = useSelectedCompanyId();
  const [result, reexecuteQuery] = useQuery({
    query: FIND_AGENDA,
    variables: { id, companyId: selectedCompanyId },
    pause: !id || id === '' || !selectedCompanyId,
    requestPolicy: 'network-only',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const CREATE_BANK_ACCOUNT = `
  mutation CREATE_BANK_ACCOUNT($agendaId: String!, $bankAccount: BankAccountInput!, $companyId: String!) {
    createBankAccount(agendaId: $agendaId, bankAccount: $bankAccount, companyId: $companyId)
  }
`;

export const CreateBankAccount = () => {
  const [result, executeMutation] = useMutation(CREATE_BANK_ACCOUNT);
  const { data, fetching, error } = result;

  return { data, fetching, error, executeMutation };
};

const EDIT_BANK_ACCOUNT = `
  mutation EDIT_BANK_ACCOUNT($agendaId: String!, $bankAccount: BankAccountInput!, $companyId: String!) {
    editBankAccount(agendaId: $agendaId, bankAccount: $bankAccount, companyId: $companyId)
  }
`;

export const EditBankAccount = () => {
  const [result, executeMutation] = useMutation(EDIT_BANK_ACCOUNT);
  const { data, fetching, error } = result;

  return { data, fetching, error, executeMutation };
};

const MAKE_MAIN_BANK_ACCOUNT = `
  mutation MAKE_MAIN_BANK_ACCOUNT($agendaId: String!, $bankAccountId: String!, $companyId: String!) {
    makeMainBankAccount(agendaId: $agendaId, bankAccountId: $bankAccountId, companyId: $companyId)
  }
`;

export const MakeMainBankAccount = () => {
  const [result, executeMutation] = useMutation(MAKE_MAIN_BANK_ACCOUNT);
  const { data, fetching, error } = result;

  return { data, fetching, error, executeMutation};
};

const DELETE_BANK_ACCOUNT = `
  mutation DELETE_BANK_ACCOUNT($agendaId: String!, $bankAccountId: String!, $companyId: String!) {
    deleteBankAccount(agendaId: $agendaId, bankAccountId: $bankAccountId, companyId: $companyId)
  }
`;

export const DeleteBankAccount = () => {
  const [result, executeMutation] = useMutation(DELETE_BANK_ACCOUNT);
  const { data, fetching, error } = result;

  return { data, fetching, error, executeMutation };
};

const UPDATE_COBRANZA_COLLECTION_MANAGEMENT = `
  mutation updateCobranzaCollectionManagement(
    $agendaId: String!
    $companyId: String!
    $input: CobranzaCollectionManagementInput!
  ) {
    updateCobranzaCollectionManagement(
      agendaId: $agendaId
      companyId: $companyId
      input: $input
    ) {
      id
      collectionAssigneeUserId
      collectionAssigneeName
      collectionAssigneeEmail
      collectionDecision
      collectionChannel
      collectionClientStatus
      collectionBusinessLine
      collectionBusinessGroup
      collectionBusinessGroupId
      collectionDocumentOverrides {
        documentId
        collectionDecision
        collectionChannel
        collectionResponse
        collectionNextSteps
      }
    }
  }
`;

export const UpdateCobranzaCollectionManagement = () => {
  const [result, executeMutation] = useMutation(UPDATE_COBRANZA_COLLECTION_MANAGEMENT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const BULK_UPDATE_COBRANZA_ASSIGNEE = `
  mutation bulkUpdateCobranzaAssignee($input: BulkCobranzaAssigneeInput!) {
    bulkUpdateCobranzaAssignee(input: $input) {
      ok
      nModified
    }
  }
`;

export const BulkUpdateCobranzaAssignee = () => {
  const [result, executeMutation] = useMutation(BULK_UPDATE_COBRANZA_ASSIGNEE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};
